import { OTPInput, REGEXP_ONLY_DIGITS, type SlotProps } from "input-otp";
import { cn } from "../lib/cn";

export interface OtpInputProps {
	value?: string;
	defaultValue?: string;
	onChange?: (value: string) => void;
	onComplete?: (value: string) => void;
	/** Шесть — длина кода из письма. Проп на случай кодов восстановления. */
	length?: number;
	hasError?: boolean;
	errorText?: string;
	disabled?: boolean;
	autoFocus?: boolean;
	/** Доступное имя поля («Код из письма»). Обязательно — умолчаний у кита нет. */
	label: string;
	className?: string;
}

/**
 * Шесть ячеек 64px r12, цифра 24px Geist Mono.
 *
 * Под капотом одно настоящее поле ввода (input-otp), а ячейки — только
 * отрисовка: так работают вставка кода из буфера, автозаполнение из СМС и
 * навигация стрелками, чего шесть отдельных input никогда не дают.
 *
 * Активная ячейка белеет и получает --ring-focus; ошибка красит все ячейки
 * сразу — код неверен целиком, а не в конкретной позиции.
 */
export function OtpInput({
	value,
	defaultValue,
	onChange,
	onComplete,
	length = 6,
	hasError = false,
	errorText,
	disabled,
	autoFocus,
	label,
	className,
}: OtpInputProps) {
	return (
		<div className={cn("flex w-full flex-col gap-1.5", className)}>
			<OTPInput
				maxLength={length}
				value={value}
				{...(defaultValue !== undefined ? { defaultValue } : {})}
				onChange={onChange}
				onComplete={onComplete}
				disabled={disabled}
				autoFocus={autoFocus}
				pattern={REGEXP_ONLY_DIGITS}
				inputMode="numeric"
				autoComplete="one-time-code"
				aria-label={label}
				aria-invalid={hasError || undefined}
				containerClassName="flex w-full items-center gap-2 has-disabled:opacity-60"
				render={({ slots }) => (
					<>
						{slots.map((slot, index) => (
							<OtpCell
								// Позиция ячейки и есть её идентичность: ячейки не переставляются.
								key={index}
								{...slot}
								hasError={hasError}
							/>
						))}
					</>
				)}
			/>
			{errorText ? (
				<span role="alert" className="t-body text-danger">
					{errorText}
				</span>
			) : null}
		</div>
	);
}

function OtpCell({ char, isActive, hasFakeCaret, hasError }: SlotProps & { hasError: boolean }) {
	return (
		<div
			className={cn(
				"relative flex h-16 flex-1 items-center justify-center rounded-card",
				"font-mono text-[24px] leading-none transition-[background-color,box-shadow] duration-150 ease-smooth",
				hasError
					? "bg-danger-soft text-fg shadow-[var(--ring-error)]"
					: isActive
						? "bg-card shadow-[var(--ring-focus)]"
						: "bg-sunken",
			)}
		>
			{char}
			{hasFakeCaret ? (
				<span
					aria-hidden="true"
					className="absolute h-6 w-px bg-fg-muted"
					style={{ animation: "iec-shimmer 1s steps(2, start) infinite" }}
				/>
			) : null}
		</div>
	);
}
